import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Fade,
  Zoom,
  Backdrop,
  Stack,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  WarningAmber,
  CheckCircle,
  DeleteForever,
  Close,
  ArrowForward,
  DeleteSweep,
} from "@mui/icons-material";

const DeleteSuccessfulTimetableByDate = ({
  isConfirmation,
  isDeletingTimeTable,
  setShowDeletTimeTableByDatePopup,
  handleDeleteLactureByDate,
}) => {
  const navigate = useNavigate();

  // Determine content based on state
  const isLoading = isDeletingTimeTable;
  const isSuccess = !isConfirmation && !isLoading;

  return (
    <Dialog
      open={true}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "visible",
          bgcolor: "transparent",
          boxShadow: "none",
        },
      }}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(8px)",
          },
        },
      }}
    >
      <Fade in={true} timeout={400}>
        <Box>
          <Zoom in={true} timeout={500}>
            <Box
              sx={{
                position: "relative",
                bgcolor: "#ffffff",
                borderRadius: 4,
                boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
            >
              {/* Top gradient bar - red for delete/danger, orange for success */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 5,
                  background: isSuccess
                    ? "linear-gradient(90deg, #f77f00, #ffb74d, #f77f00)"
                    : "linear-gradient(90deg, #d32f2f, #ef5350, #d32f2f)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 2s infinite linear",
                  "@keyframes shimmer": {
                    "0%": { backgroundPosition: "200% 0" },
                    "100%": { backgroundPosition: "-200% 0" },
                  },
                }}
              />

              {/* Background decorative icon */}
              {!isLoading && (
                <DeleteSweep
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 20,
                    color: "#fff3e0",
                    fontSize: 80,
                    transform: "rotate(15deg)",
                    opacity: 0.4,
                  }}
                />
              )}

              <DialogContent sx={{ p: 4, pt: 5, textAlign: "center" }}>
                {isLoading ? (
                  // ─── LOADING STATE ───
                  <Stack spacing={3} alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress
                      size={70}
                      thickness={4}
                      sx={{ color: "#f77f00" }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      Deleting lectures...
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please wait while we remove the timetable entries
                    </Typography>
                  </Stack>
                ) : isConfirmation ? (
                  // ─── CONFIRMATION STATE ───
                  <Stack spacing={3} alignItems="center">
                    {/* Warning Icon */}
                    <Box
                      sx={{
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: -8,
                          left: -8,
                          right: -8,
                          bottom: -8,
                          borderRadius: "50%",
                          border: "3px solid #ffebee",
                          animation:
                            "pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite",
                          "@keyframes pulse-ring": {
                            "0%": { transform: "scale(0.8)", opacity: 1 },
                            "100%": { transform: "scale(1.3)", opacity: 0 },
                          },
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 90,
                          height: 90,
                          bgcolor: "#ffebee",
                          boxShadow: "0 8px 24px rgba(211,47,47,0.2)",
                        }}
                      >
                        <WarningAmber
                          sx={{
                            fontSize: 50,
                            color: "#d32f2f",
                            animation:
                              "popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                            "@keyframes popIn": {
                              "0%": { transform: "scale(0)", opacity: 0 },
                              "100%": { transform: "scale(1)", opacity: 1 },
                            },
                          }}
                        />
                      </Avatar>
                    </Box>

                    {/* Title */}
                    <Typography variant="h5" fontWeight={800} color="#2d3436">
                      Confirm Deletion
                    </Typography>

                    {/* Message */}
                    <Box sx={{ maxWidth: 320 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7, fontSize: "1.05rem" }}
                      >
                        Do you want to delete{" "}
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#d32f2f" }}
                        >
                          all lectures
                        </Box>{" "}
                        for the given date range?
                      </Typography>
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "#ffebee",
                          border: "1px dashed #ef9a9a",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="#c62828"
                          fontWeight={600}
                        >
                          ⚠️ This action cannot be undone.
                        </Typography>
                      </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ width: "100%", mt: 1 }}
                    >
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={() => setShowDeletTimeTableByDatePopup(false)}
                        startIcon={<Close />}
                        sx={{
                          borderRadius: 3,
                          py: 1.2,
                          fontWeight: 700,
                          textTransform: "none",
                          borderColor: "#e0e0e0",
                          color: "#616161",
                          "&:hover": {
                            borderColor: "#bdbdbd",
                            bgcolor: "#f5f5f5",
                          },
                        }}
                      >
                        No, Cancel
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleDeleteLactureByDate}
                        startIcon={<DeleteForever />}
                        sx={{
                          borderRadius: 3,
                          py: 1.2,
                          fontWeight: 700,
                          textTransform: "none",
                          background:
                            "linear-gradient(135deg, #d32f2f 0%, #ef5350 100%)",
                          boxShadow: "0 8px 24px rgba(211,47,47,0.3)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)",
                            boxShadow: "0 12px 32px rgba(211,47,47,0.4)",
                            transform: "translateY(-1px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Yes, Delete
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  // ─── SUCCESS STATE ───
                  <Stack spacing={3} alignItems="center">
                    {/* Success Icon */}
                    <Box
                      sx={{
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: -8,
                          left: -8,
                          right: -8,
                          bottom: -8,
                          borderRadius: "50%",
                          border: "3px solid #fff3e0",
                          animation:
                            "pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite",
                          "@keyframes pulse-ring": {
                            "0%": { transform: "scale(0.8)", opacity: 1 },
                            "100%": { transform: "scale(1.3)", opacity: 0 },
                          },
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 90,
                          height: 90,
                          bgcolor: "#fff3e0",
                          boxShadow: "0 8px 24px rgba(247,127,0,0.25)",
                        }}
                      >
                        <CheckCircle
                          sx={{
                            fontSize: 50,
                            color: "#f77f00",
                            animation:
                              "popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                            "@keyframes popIn": {
                              "0%": { transform: "scale(0)", opacity: 0 },
                              "100%": { transform: "scale(1)", opacity: 1 },
                            },
                          }}
                        />
                      </Avatar>
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      color="#2d3436"
                      sx={{ letterSpacing: "-0.5px" }}
                    >
                      Deleted Successfully!
                    </Typography>

                    {/* Message */}
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        maxWidth: 280,
                        lineHeight: 1.7,
                        fontSize: "1.05rem",
                      }}
                    >
                      You have successfully deleted the timetable entries for
                      the selected date range.
                    </Typography>

                    {/* Action Button */}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => setShowDeletTimeTableByDatePopup(false)}
                      endIcon={<ArrowForward />}
                      sx={{
                        mt: 2,
                        px: 5,
                        py: 1.2,
                        borderRadius: 3,
                        fontWeight: 700,
                        fontSize: "1rem",
                        textTransform: "none",
                        background:
                          "linear-gradient(135deg, #f77f00 0%, #fb8c00 100%)",
                        boxShadow: "0 8px 24px rgba(247,127,0,0.35)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 12px 32px rgba(247,127,0,0.45)",
                          background:
                            "linear-gradient(135deg, #e65100 0%, #f77f00 100%)",
                        },
                      }}
                    >
                      Okay
                    </Button>
                  </Stack>
                )}
              </DialogContent>
            </Box>
          </Zoom>
        </Box>
      </Fade>
    </Dialog>
  );
};

export default DeleteSuccessfulTimetableByDate;
